import { initRazorpayCheckout } from "./paymentCheck";
import { cardPayment, verifyPayment } from "src/shared/services/servicesv2";
import { closeLoading, notifyError, notifyLoading } from "src/assets/toastService";

export interface RazorpayPaymentDetails {
    transactionId: string;
    paymentId: string;
    razorpayOrderId: string;
    razorpaySignature: string;
    status: string;
    method: string;
    bank?: string;
    wallet?: string;
    vpa?: string;
    email?: string;
    contact?: string;
    fee?: number;
    tax?: number;
    currency?: string;
    amount: number;
}

interface ProcessCardPaymentArgs {
    /** Amount in rupees (will be converted to paise internally) */
    amount: number;
    description: string;
    onSuccess: (paymentDetails: RazorpayPaymentDetails) => Promise<void> | void;
    onDismiss?: () => void;
    onError?: (error: unknown) => void;
}

/**
 * Shared Razorpay card-payment flow.
 * Handles: script load -> create order -> open checkout -> verify -> callback.
 * Callers are responsible for what happens to their own order/ledger record
 * (that's the `onSuccess` callback).
 */
export const processCardPayment = async ({
    amount,
    description,
    onSuccess,
    onDismiss,
    onError,
}: ProcessCardPaymentArgs): Promise<void> => {
    try {
        const loaded = await initRazorpayCheckout();
        if (!loaded) {
            notifyError("Razorpay failed to load");
            return;
        }

        notifyLoading("Loading payment...");

        const paymentData = await cardPayment({
            amount: Math.round(amount * 100),
        });

        closeLoading();
        notifyLoading("Processing card payment...");

        const options = {
            key: paymentData.key,
            amount: paymentData.amount,
            currency: paymentData.currency,
            order_id: paymentData.order_id,
            name: "POS System",
            description,

            handler: async (response: {
                razorpay_order_id: string;
                razorpay_payment_id: string;
                razorpay_signature: string;
            }) => {
                try {
                    closeLoading();
                    notifyLoading("Verifying payment...")
                    const verifyData = await verifyPayment({
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                    });

                    if (!verifyData.payment) {
                        notifyError("Payment verification failed");
                        return;
                    }

                    const payment = verifyData.payment;

                    const paymentDetails: RazorpayPaymentDetails = {
                        transactionId: payment.acquirer_data,
                        paymentId: response.razorpay_payment_id,
                        razorpayOrderId: response.razorpay_order_id,
                        razorpaySignature: response.razorpay_signature,
                        status: payment.status,
                        method: payment.method,
                        bank: payment.bank,
                        wallet: payment.wallet,
                        vpa: payment.vpa,
                        email: payment.email,
                        contact: payment.contact,
                        fee: payment.fee,
                        tax: payment.tax,
                        currency: payment.currency,
                        amount: payment.amount,
                    };
                    await onSuccess(paymentDetails);
                } catch (err) {
                    console.error("Verification error:", err);
                    notifyError("Payment verification failed");
                    onError?.(err);
                    closeLoading();
                } finally {
                    closeLoading();
                }
            },

            modal: {
                ondismiss: () => {
                    notifyError("Payment Cancelled");
                    closeLoading();
                    onDismiss?.();
                },
            },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();
    } catch (err) {
        console.error(err);
        closeLoading();
        notifyError("Payment failed");
        onError?.(err);
    }
};